import type { AgentId, ConnectionState, MeetingInputBoundary, MeetingInputHealth, ObserverWorkActivity, SessionMode, UserSettings } from "@basket/protocol";
import { agentCatalog, audioModeFor, nextAudioMode } from "@basket/protocol";
import {
  AudioLines,
  ChevronRight,
  Ear,
  Headphones,
  History,
  LogOut,
  MessageCircle,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Send,
  Settings,
  VolumeX,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";

export type AgentAsset = {
  portrait: string;
  sprite: string;
};

export type HistoryItem = {
  id: string;
  agent: AgentId;
  label: string;
  timestamp: string;
};

export type ChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
};

export type BasketShellProps = {
  agents: AgentId[];
  assets: Record<AgentId, AgentAsset>;
  brandMark?: string;
  brandLogo?: string;
  selectedAgent: AgentId | null;
  connectionState: ConnectionState;
  errorMessage?: string;
  mode: SessionMode;
  active: boolean;
  micMuted: boolean;
  speakerMuted: boolean;
  meet?: boolean;
  captureActive?: boolean;
  audioGateOpen?: boolean;
  messages: ChatMessage[];
  observerActivities?: ObserverWorkActivity[];
  meetingInputBoundary?: MeetingInputBoundary;
  meetingInputHealth?: MeetingInputHealth;
  history: HistoryItem[];
  onSelectAgent: (agent: AgentId) => void;
  onSelectTop?: () => void;
  onToggleSession: () => void;
  onToggleMic: () => void;
  onCycleAudioMode: () => void;
  onSendMessage: (text: string) => void;
  onSelectHistory?: (id: string) => void;
  onWakeAgent?: (agent: AgentId, source: "double_click" | "long_press") => void;
  onCloseThread?: () => void;
  threadOpen?: boolean;
  settings?: UserSettings;
  managedProfile?: { displayName: string; email: string };
  usageSummary?: Record<string, unknown>;
  onSaveSettings?: (settings: UserSettings) => void | Promise<void>;
  onPreviewBgm?: (enabled: boolean, volume: number) => void;
  onLogout?: () => void;
  onLogoutAll?: () => void;
  extensionDownloadUrl?: string;
  onToggleCapture?: () => void;
  onToggleAudioGate?: () => void;
  onOpenSettings?: () => void;
};

const stateLabels: Record<ConnectionState, string> = {
  idle: "いつでも通話してください",
  connecting: "接続しています…",
  listening: "聞いています",
  thinking: "確認しています…",
  speaking: "話しています",
  error: "接続を確認してください",
};

const observerActivityLabels: Record<ObserverWorkActivity["state"], string> = {
  queued: "準備中",
  running: "確認しています",
  completed: "確認できました",
  failed: "確認できませんでした",
};

function AgentSprite({ agent, src, selected }: { agent: AgentId; src: string; selected: boolean }) {
  return (
    <span
      className={`basket-sprite basket-sprite--${agent}${selected ? " is-selected" : ""}`}
      aria-hidden="true"
    >
      <img key={selected ? "selected" : "idle"} src={src} alt="" draggable={false} />
    </span>
  );
}

function AgentSwitcher({
  agents,
  assets,
  selectedAgent,
  onSelectAgent,
  brandMark,
  brandLogo,
  onSelectTop,
  onWakeAgent,
}: Pick<BasketShellProps, "agents" | "assets" | "brandMark" | "brandLogo" | "selectedAgent" | "onSelectAgent" | "onSelectTop" | "onWakeAgent">) {
  const pressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pressOrigin = useRef<{ x: number; y: number } | undefined>(undefined);
  const suppressClick = useRef<AgentId | undefined>(undefined);
  const buttonRefs = useRef(new Map<AgentId, HTMLButtonElement>());
  const [indicatorStyle, setIndicatorStyle] = useState<CSSProperties>({ opacity: 0 });
  useEffect(() => {
    const update = () => {
      const button = selectedAgent ? buttonRefs.current.get(selectedAgent) : undefined;
      if (!button) { setIndicatorStyle({ opacity: 0 }); return; }
      const size = window.matchMedia("(max-width: 720px)").matches ? 57 : 76;
      setIndicatorStyle({
        opacity: 1,
        width: size,
        height: size,
        transform: `translate3d(${button.offsetLeft + (button.offsetWidth - size) / 2}px, ${button.offsetTop + (button.offsetHeight - size) / 2}px, 0)`,
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [selectedAgent, agents]);
  const cancelPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = undefined;
    pressOrigin.current = undefined;
  };
  return (
    <nav className="basket-agent-nav" aria-label="Basketメンバー">
      <button type="button" className="basket-brand" aria-label="通話を終了してBasketのTOPへ戻る" onClick={onSelectTop}>
        {brandLogo ? <img className="basket-brand-logo" src={brandLogo} alt="Basket" /> : null}
        {brandMark ? <img className="basket-brand-mark" src={brandMark} alt={brandLogo ? "" : "Basket"} /> : <span className="basket-brand-mark">B</span>}
      </button>
      <div className="basket-agent-scroll">
        <span className="basket-agent-indicator" style={indicatorStyle} aria-hidden="true" />
        {agents.map((agent) => {
          const selected = selectedAgent === agent;
          return (
            <button
              type="button"
              ref={(element) => { if (element) buttonRefs.current.set(agent, element); else buttonRefs.current.delete(agent); }}
              className={`basket-agent-button${selected ? " is-selected" : ""}`}
              aria-pressed={selected}
              onClick={() => {
                if (suppressClick.current === agent) { suppressClick.current = undefined; return; }
                onSelectAgent(agent);
              }}
              onDoubleClick={() => onWakeAgent?.(agent, "double_click")}
              onPointerDown={(event) => {
                if (event.pointerType === "mouse") return;
                pressOrigin.current = { x: event.clientX, y: event.clientY };
                pressTimer.current = setTimeout(() => {
                  pressTimer.current = undefined;
                  suppressClick.current = agent;
                  onWakeAgent?.(agent, "long_press");
                }, 500);
              }}
              onPointerMove={(event) => {
                const origin = pressOrigin.current;
                if (origin && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > 10) cancelPress();
              }}
              onPointerUp={cancelPress}
              onPointerCancel={cancelPress}
              key={agent}
            >
              <AgentSprite agent={agent} src={assets[agent].sprite} selected={selected} />
              <span className="basket-agent-name">{agentCatalog[agent].name}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function IconButton({ label, active = false, danger = false, success = false, expanded, children, onClick }: {
  label: string;
  active?: boolean;
  danger?: boolean;
  success?: boolean;
  expanded?: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`basket-icon-button${active ? " is-active" : ""}${danger ? " is-danger" : ""}${success ? " is-success" : ""}`}
      aria-label={label}
      {...(typeof expanded === "boolean" ? { "aria-expanded": expanded } : {})}
      title={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function ControlDock(props: Pick<BasketShellProps,
  "active" | "micMuted" | "speakerMuted" | "mode" | "onToggleSession" | "onToggleMic" | "onCycleAudioMode" | "onSendMessage"
> & { selectedAgent: AgentId; chatOpen: boolean; onToggleChat: () => void }) {
  const [draft, setDraft] = useState("");
  const [feedback, setFeedback] = useState<{ id: number; text: string }>();
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const feedbackId = useRef(0);
  const previousActive = useRef(props.active);
  const audioMode = audioModeFor(props.mode, props.speakerMuted);
  const nextMode = nextAudioMode(audioMode);
  const audioModeLabels = { conversation: "会話", observer: "陪席", muted: "ミュート" } as const;
  const audioModeFeedback = { conversation: "会話モードになりました", observer: "陪席モードになりました", muted: "ミュートになりました" } as const;
  const showFeedback = (text: string) => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackId.current += 1;
    setFeedback({ id: feedbackId.current, text });
    feedbackTimer.current = setTimeout(() => setFeedback(undefined), 2_000);
  };
  useEffect(() => {
    if (!previousActive.current && props.active) showFeedback("通話を開始しました");
    previousActive.current = props.active;
  }, [props.active]);
  useEffect(() => () => { if (feedbackTimer.current) clearTimeout(feedbackTimer.current); }, []);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) return;
    props.onSendMessage(value);
    setDraft("");
    showFeedback("メッセージを送信しました");
  };

  return (
    <div className={`basket-control-wrap${props.chatOpen ? " is-chat" : ""}`}>
      {feedback ? <div className="basket-control-feedback" key={feedback.id} role="status" aria-live="polite">{feedback.text}</div> : null}
      <div className="basket-control-dock">
        <IconButton
          label={`音声モード: ${audioModeLabels[audioMode]}。押すと${audioModeLabels[nextMode]}`}
          active={audioMode !== "muted"}
          onClick={() => { props.onCycleAudioMode(); showFeedback(audioModeFeedback[nextMode]); }}
        >
          {audioMode === "conversation" ? <AudioLines /> : audioMode === "observer" ? <Ear /> : <VolumeX />}
        </IconButton>
        <IconButton label={props.micMuted ? "マイクをオン" : "マイクをオフ"} active={!props.micMuted} onClick={() => { props.onToggleMic(); showFeedback(props.micMuted ? "マイクをオンにしました" : "マイクをミュートしました"); }}>
          {props.micMuted ? <MicOff /> : <Mic />}
        </IconButton>
        <IconButton label={props.active ? "会話を終了" : "会話を開始"} active={props.active} danger={props.active} success={!props.active} onClick={() => { props.onToggleSession(); showFeedback(props.active ? "通話を終了しました" : "通話を開始しています…"); }}>
          {props.active ? <PhoneOff /> : <Phone />}
        </IconButton>
        <form className="basket-dock-composer" onSubmit={submit} aria-hidden={!props.chatOpen}>
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={`${agentCatalog[props.selectedAgent].name}にメッセージ`}
            aria-label="メッセージ"
            disabled={!props.chatOpen}
            tabIndex={props.chatOpen ? 0 : -1}
          />
          <button type="submit" aria-label="送信" disabled={!props.chatOpen} tabIndex={props.chatOpen ? 0 : -1}><Send /></button>
        </form>
        <IconButton
          label={props.chatOpen ? "音声画面に戻る" : "チャットを開く"}
          active={props.chatOpen}
          expanded={props.chatOpen}
          onClick={() => { props.onToggleChat(); showFeedback(props.chatOpen ? "音声画面に戻りました" : "チャットを開きました"); }}
        >
          {props.chatOpen ? <Headphones /> : <MessageCircle />}
        </IconButton>
      </div>
    </div>
  );
}

function ChatPanel({ messages, open }: Pick<BasketShellProps, "messages"> & { open: boolean }) {
  const listRef = useRef<HTMLDivElement>(null);
  const followingRef = useRef(true);
  const previousLength = useRef(0);
  const [newMessagePending, setNewMessagePending] = useState(false);
  useEffect(() => {
    const added = messages.length > previousLength.current;
    const latest = messages.at(-1);
    previousLength.current = messages.length;
    if (!added || !latest) return;
    if (latest.role === "user" || followingRef.current) {
      requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }));
      setNewMessagePending(false);
    } else if (latest.role === "assistant") {
      setNewMessagePending(true);
    }
  }, [messages]);

  return (
    <section className="basket-chat" aria-label="チャット" aria-hidden={!open}>
      <div
        className="basket-chat-list"
        ref={listRef}
        aria-live="polite"
        onScroll={() => {
          const element = listRef.current;
          if (!element) return;
          followingRef.current = element.scrollHeight - element.scrollTop - element.clientHeight <= 48;
          if (followingRef.current) setNewMessagePending(false);
        }}
      >
        {messages.map((message) => (
          <div className={`basket-message is-${message.role}`} key={message.id}>{message.text}</div>
        ))}
      </div>
      {newMessagePending ? (
        <button type="button" className="basket-new-message" onClick={() => {
          followingRef.current = true;
          setNewMessagePending(false);
          listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
        }}>新しいメッセージ</button>
      ) : null}
    </section>
  );
}

function HistoryPanel({
  open,
  onClose,
  history,
  assets,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  history: HistoryItem[];
  assets: Record<AgentId, AgentAsset>;
  onSelect?: (id: string) => void;
}) {
  return (
    <aside className={`basket-history${open ? " is-open" : ""}`} aria-hidden={!open}>
      <header>
        <h2>最近の会話</h2>
        <button type="button" aria-label="閉じる" onClick={onClose}><X /></button>
      </header>
      <div className="basket-history-list">
        {history.length === 0 ? <p>まだ会話はありません。</p> : history.map((item) => (
          <button type="button" key={item.id} onClick={() => onSelect?.(item.id)}>
            <img src={assets[item.agent].portrait} alt="" />
            <span><strong>{item.label}</strong><small>{agentCatalog[item.agent].name} · {item.timestamp}</small></span>
          </button>
        ))}
      </div>
    </aside>
  );
}

function SettingsPanel({ open, onClose, settings, managedProfile, usageSummary, extensionDownloadUrl, onSave, onPreviewBgm, onLogout, onLogoutAll }: {
  open: boolean;
  onClose: (draft?: UserSettings) => void;
  settings?: UserSettings;
  managedProfile?: { displayName: string; email: string };
  usageSummary?: Record<string, unknown>;
  extensionDownloadUrl?: string;
  onSave?: (settings: UserSettings) => void;
  onPreviewBgm?: (enabled: boolean, volume: number) => void;
  onLogout?: () => void;
  onLogoutAll?: () => void;
}) {
  const [draft, setDraft] = useState<UserSettings | undefined>(settings);
  useEffect(() => setDraft(settings), [settings]);
  const updateProfile = (key: keyof UserSettings["profile"], value: string) => {
    setDraft((current) => current ? { ...current, profile: { ...current.profile, [key]: value } } : current);
  };
  const usageValue = (key: string) => typeof usageSummary?.[key] === "number" ? usageSummary[key] as number : 0;
  const close = () => {
    if (draft) void Promise.resolve(onSave?.(draft)).catch((error) => console.error("[basket] settings save failed", error));
    onClose(draft);
  };
  return <>
    <aside className={`basket-settings${open ? " is-open" : ""}`} aria-hidden={!open}>
      <header><h2>設定</h2><button type="button" aria-label="閉じる（変更を保存）" onClick={close}><X /></button></header>
      {draft ? <div className="basket-settings-body">
        <section className="basket-settings-disclosure"><details>
          <summary><span>プロフィール</span><ChevronRight aria-hidden="true" /></summary>
          <div className="basket-settings-detail"><small>{managedProfile?.displayName} · {managedProfile?.email}</small>
            <label>呼ばれたい名前<input value={draft.profile.preferredName} onChange={(event) => updateProfile("preferredName", event.target.value)} /></label>
            <label>役割<input value={draft.profile.role} onChange={(event) => updateProfile("role", event.target.value)} /></label>
            <label>担当<textarea value={draft.profile.responsibilities} onChange={(event) => updateProfile("responsibilities", event.target.value)} /></label>
            <label>自己紹介<textarea value={draft.profile.selfIntro} onChange={(event) => updateProfile("selfIntro", event.target.value)} /></label>
            <label>会議で紹介してよい内容<textarea value={draft.profile.publicIntro} onChange={(event) => updateProfile("publicIntro", event.target.value)} /></label>
            <label>内部参照だけに使う情報<textarea value={draft.profile.internalProfile} onChange={(event) => updateProfile("internalProfile", event.target.value)} /></label>
          </div>
        </details></section>
        <section className="basket-settings-disclosure"><details>
          <summary><span>声</span><ChevronRight aria-hidden="true" /></summary>
          <div className="basket-settings-detail">{Object.keys(draft.voiceByAgent).map((agent) => <label key={agent}>{agentCatalog[agent as AgentId].name}<select value={draft.voiceByAgent[agent as AgentId]} onChange={(event) => setDraft((current) => current ? { ...current, voiceByAgent: { ...current.voiceByAgent, [agent]: event.target.value } as UserSettings["voiceByAgent"] } : current)}>{["alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse", "marin", "cedar"].map((voice) => <option key={voice}>{voice}</option>)}</select></label>)}</div>
        </details></section>
        <section><div className="basket-settings-heading"><h3>BGM</h3><label className="basket-check"><input type="checkbox" checked={draft.bgmEnabled} onChange={(event) => { const enabled = event.target.checked; setDraft({ ...draft, bgmEnabled: enabled }); onPreviewBgm?.(enabled, draft.bgmVolume); }} /><span>{draft.bgmEnabled ? "ON" : "OFF"}</span></label></div><label className="basket-volume-control"><span>音量</span><input type="range" min="0" max="0.2" step="0.005" value={draft.bgmVolume} onChange={(event) => { const volume = Number(event.target.value); setDraft({ ...draft, bgmVolume: volume }); onPreviewBgm?.(draft.bgmEnabled, volume); }} /></label></section>
        <section><h3>使用量（今月）</h3>{usageSummary ? <div className="basket-usage-grid"><span><strong>{usageValue("sessionCount")}</strong>セッション</span><span><strong>{usageValue("voiceMinutes").toFixed(1)}</strong>音声・分</span><span><strong>{usageValue("textMessages")}</strong>メッセージ</span><span><strong>{usageValue("codexTasks")}</strong>実務依頼</span><span><strong>{usageValue("observerResearch")}</strong>陪席調査</span><span><strong>${usageValue("estimatedCostUsd").toFixed(3)}</strong>概算</span></div> : <small>集計中…</small>}<small>料金はBasket内部集計による概算です。</small></section>
        <section><h3>Chrome拡張</h3><div className="basket-settings-links">{extensionDownloadUrl ? <a href={extensionDownloadUrl} download>ダウンロード</a> : null}<a href="/extension">接続コードを発行</a></div><small>Chromeへ読み込んだ後、接続コードを入力します。</small></section>
        <section><h3>アカウント</h3><button type="button" onClick={onLogout}><LogOut />ログアウト</button><button type="button" onClick={onLogoutAll}>すべての端末からログアウト</button></section>
        <section className="basket-member-directory"><a href={typeof window !== "undefined" && window.location.protocol === "chrome-extension:" ? "https://basket.lemo.dev/members" : "/members"} target="_blank" rel="noreferrer">basket メンバー紹介へ<span aria-hidden="true">↗</span></a></section>
      </div> : null}
    </aside>
    {open ? <button className="basket-scrim" type="button" aria-label="閉じる（変更を保存）" onClick={close} /> : null}
  </>;
}

export function BasketShell(props: BasketShellProps) {
  const [chatOpen, setChatOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const closeSettings = (draft?: UserSettings) => {
    if (draft) {
      props.onPreviewBgm?.(draft.bgmEnabled, draft.bgmVolume);
    } else if (props.settings) {
      props.onPreviewBgm?.(props.settings.bgmEnabled, props.settings.bgmVolume);
    }
    setSettingsOpen(false);
  };
  const selected = props.selectedAgent ? agentCatalog[props.selectedAgent] : undefined;
  const preferredName = props.settings?.profile.preferredName?.trim() || props.managedProfile?.displayName || "ゲスト";
  const topDeviceNotices = [
    props.micMuted ? "マイクがOFFになってます。" : undefined,
    props.speakerMuted ? "スピーカーがOFFになってます。" : undefined,
  ].filter((notice): notice is string => Boolean(notice));
  const status = useMemo(() => {
    if (props.errorMessage) return props.errorMessage;
    if (props.speakerMuted && props.connectionState === "listening") return "音声をミュートしています";
    if (props.mode === "observer" && props.connectionState === "listening") return "陪席しています。呼ばれるまで話しません";
    return stateLabels[props.connectionState];
  }, [props.connectionState, props.errorMessage, props.mode, props.speakerMuted]);

  return (
    <div className={`basket-app${props.meet ? " is-meet" : ""}`}>
      <AgentSwitcher
        agents={props.agents}
        assets={props.assets}
        selectedAgent={props.selectedAgent}
        onSelectAgent={props.onSelectAgent}
        {...(props.onSelectTop ? {
          onSelectTop: () => {
            setChatOpen(false);
            props.onSelectTop?.();
          },
        } : {})}
        {...(props.onWakeAgent ? { onWakeAgent: props.onWakeAgent } : {})}
        {...(props.brandMark ? { brandMark: props.brandMark } : {})}
        {...(props.brandLogo ? { brandLogo: props.brandLogo } : {})}
      />
      <div className="basket-top-actions"><button type="button" onClick={() => setHistoryOpen(true)} aria-label="最近の会話"><History /></button><button type="button" onClick={() => { props.onOpenSettings?.(); setSettingsOpen(true); }} aria-label="設定"><Settings /></button></div>

      <main className="basket-main">
        <section className={`basket-stage${chatOpen ? " is-chat" : ""}`}>
          <div className={`basket-voice-stage${selected ? "" : " is-top"}`} aria-hidden={chatOpen}>
            {selected && props.selectedAgent ? <>
              <button
                type="button"
                className={`basket-portrait state-${props.connectionState}`}
                aria-label={props.active ? `${selected.name}と通話中` : `${selected.name}と通話を開始`}
                disabled={props.active || chatOpen}
                onClick={props.onToggleSession}
              >
                <img src={props.assets[props.selectedAgent].portrait} alt={`${selected.name} ${selected.reading}`} />
              </button>
              <h1>{selected.name}<span>{selected.reading}</span></h1>
              <p className="basket-status" aria-live="polite">{status}</p>
              {props.meet && props.active && props.meetingInputBoundary && props.meetingInputBoundary.status !== "inactive" ? (
                <section className={`basket-meeting-boundary status-${props.meetingInputBoundary.status}`} aria-label="会議音声の権限境界" aria-live="polite">
                  <strong>{["account", "participant"].includes(props.meetingInputBoundary.participantAttribution) ? "話者分離中" : "安全な話者境界"}</strong>
                  <span>{props.meetingInputBoundary.message}</span>
                </section>
              ) : null}
              {props.meet && props.active && props.meetingInputHealth && props.meetingInputHealth.participantCount > 1 ? (
                <section className={`basket-meeting-health status-${props.meetingInputHealth.status}`} aria-label="参加者音声の受信状態" aria-live="polite">
                  <strong>{props.meetingInputHealth.status === "transcript_confirmed"
                    ? props.meetingInputHealth.remoteParticipantConfirmed ? "参加者音声を確認済み" : "会議音声を確認済み"
                    : props.meetingInputHealth.status === "failed" ? "参加者音声を確認できません"
                    : props.meetingInputHealth.status === "speech_detected" ? "参加者音声を文字起こし中"
                    : props.meetingInputHealth.status === "signal_detected" ? "参加者音声の信号を検出"
                    : "参加者音声を待っています"}</strong>
                  <span>{props.meetingInputHealth.status === "transcript_confirmed"
                    ? props.meetingInputHealth.remoteParticipantConfirmed
                      ? `Meetから受信し、文字起こしまで確認しました（${props.meetingInputHealth.transcriptCount}件）。`
                      : "Meet音声の文字起こしは確認しましたが、参加者への帰属は未確定です。"
                    : props.meetingInputHealth.status === "failed"
                      ? props.meetingInputHealth.failureReason === "remote_speaker_without_audio"
                        ? "Meet上では別参加者が発話していますが、音声信号がBasketへ届いていません。"
                        : props.meetingInputHealth.failureReason === "speech_without_transcript"
                          ? "音声信号は届いていますが、文字起こしが完了していません。"
                          : "会議音声の入力経路が停止しました。"
                      : props.meetingInputHealth.status === "speech_detected"
                        ? "発話区間を検出しました。文字起こしの完了を確認しています。"
                        : props.meetingInputHealth.status === "signal_detected"
                          ? "Meetから非無音信号が届いています。発話内容の確認待ちです。"
                          : "別参加者が話すと、音声信号と文字起こしを段階別に確認します。"}</span>
                </section>
              ) : null}
              {props.active && props.mode === "observer" && props.observerActivities?.length ? (
                <section className="basket-observer-activities" aria-label={`${selected.name}が裏側で進めている準備`} aria-live="polite">
                  <strong>裏側の準備</strong>
                  <ul>{props.observerActivities.slice(0, 3).map((activity) => (
                    <li className={`state-${activity.state}`} key={activity.activityId}>
                      <span>{activity.summary}</span>
                      <small>{observerActivityLabels[activity.state]}</small>
                    </li>
                  ))}</ul>
                </section>
              ) : null}
            </> : <><img className="basket-top-logo" src={props.brandLogo ?? props.brandMark} alt="Basket" /><p className="basket-status">ようこそ {preferredName}</p></>}
          </div>
          <ChatPanel messages={props.messages} open={chatOpen} />
        </section>

        {props.selectedAgent ? <ControlDock
          active={props.active}
          micMuted={props.micMuted}
          speakerMuted={props.speakerMuted}
          mode={props.mode}
          selectedAgent={props.selectedAgent}
          chatOpen={chatOpen}
          onToggleSession={props.onToggleSession}
          onToggleMic={props.onToggleMic}
          onCycleAudioMode={props.onCycleAudioMode}
          onSendMessage={props.onSendMessage}
          onToggleChat={() => setChatOpen((value) => !value)}
        /> : null}
      </main>

      {props.selectedAgent && props.threadOpen && (props.active || props.messages.length > 0) && props.onCloseThread ? <button type="button" className="basket-thread-close" onClick={props.onCloseThread}>Close</button> : null}
      {!props.selectedAgent && topDeviceNotices.length > 0 ? <div className="basket-top-device-status" role="status" aria-live="polite">
        {topDeviceNotices.map((notice) => <p key={notice}><span aria-hidden="true">×</span>{notice}</p>)}
      </div> : null}

      <HistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={props.history}
        assets={props.assets}
        {...(props.onSelectHistory ? {
          onSelect: (id: string) => {
            setHistoryOpen(false);
            setChatOpen(true);
            props.onSelectHistory?.(id);
          },
        } : {})}
      />
      <SettingsPanel
        open={settingsOpen}
        onClose={closeSettings}
        {...(props.settings ? { settings: props.settings } : {})}
        {...(props.managedProfile ? { managedProfile: props.managedProfile } : {})}
        {...(props.usageSummary ? { usageSummary: props.usageSummary } : {})}
        {...(props.extensionDownloadUrl ? { extensionDownloadUrl: props.extensionDownloadUrl } : {})}
        {...(props.onSaveSettings ? { onSave: props.onSaveSettings } : {})}
        {...(props.onPreviewBgm ? { onPreviewBgm: props.onPreviewBgm } : {})}
        {...(props.onLogout ? { onLogout: props.onLogout } : {})}
        {...(props.onLogoutAll ? { onLogoutAll: props.onLogoutAll } : {})}
      />
      {historyOpen ? <button className="basket-scrim" type="button" aria-label="閉じる" onClick={() => setHistoryOpen(false)} /> : null}
    </div>
  );
}
